import { useState } from "react";
import { useQuery, useMutation, useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Key, Plus, Trash2, Copy, Check, BarChart3, CreditCard, Clock, AlertCircle, ListMusic, ChevronDown, X } from "lucide-react";
import { SEO } from "../components/SEO";

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function Dashboard() {
  const keys = useQuery(api.mcpPortal.getMyApiKeys);
  const stats = useQuery(api.mcpPortal.getMyUsageStats);
  const subscription = useQuery(api.mcpPortal.getMySubscription);
  const usageHistory = useQuery(api.mcpPortal.getMyUsageHistory);
  const playlists = useQuery(api.mcpPortal.getMyPlaylists);
  const createKey = useMutation(api.mcpPortal.createMyApiKey);
  const revokeKey = useMutation(api.mcpPortal.revokeMyApiKey);
  const deleteKey = useMutation(api.mcpPortal.deleteMyApiKey);
  const cancelSub = useAction(api.mcpStripe.cancelSubscription);
  const createBillingPortal = useAction(api.mcpStripe.createBillingPortalSession);

  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [logsLimit, setLogsLimit] = useState(25);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const requestLogs = useQuery(api.mcpPortal.getMyRequestLogs, { limit: logsLimit });

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const result = await createKey({ name: newKeyName.trim() });
      setCreatedKey(result.key);
      setNewKeyName("");
    } catch (err: any) {
      alert(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleRevoke = async (keyId: string) => {
    if (!confirm("Are you sure you want to revoke this API key? This action cannot be undone.")) return;
    try {
      await revokeKey({ keyId });
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleDelete = async (keyId: string) => {
    if (!confirm("Permanently delete this revoked API key? This action cannot be undone.")) return;
    setDeletingKeyId(keyId);
    try {
      await deleteKey({ keyId });
    } catch (err: any) {
      alert(err.message);
    } finally {
      setDeletingKeyId(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm("Cancel your subscription? You'll keep Pro access until the end of your billing period, then downgrade to Free.")) return;
    setCancelling(true);
    try {
      await cancelSub();
      alert("Subscription cancelled. You'll have Pro access until the end of your billing period.");
    } catch (err: any) {
      alert(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const result = await createBillingPortal({
        returnUrl: window.location.href,
      });
      window.location.href = result.url;
    } catch (err: any) {
      alert(err.message);
    }
  };

  const tier = subscription?.tier ?? "free";

  // Max API keys allowed per tier
  const MAX_KEYS: Record<string, number> = {
    free: 1,
    pro: 5,
    business: 20,
    enterprise: 50,
  };

  const maxKeys = MAX_KEYS[tier] ?? 1;
  const activeKeysCount = stats?.activeKeys ?? 0;
  const atKeyLimit = activeKeysCount >= maxKeys;
  const hasActiveSubscription = subscription?.status === "active" && tier !== "free";
  const isCancelling = subscription?.status === "canceled";

  return (
    <>
      <SEO
        title="Dashboard | StarSinger MCP"
        description="Manage your StarSinger MCP API keys, view usage stats, and monitor your integration."
        pathname="/dashboard"
      />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold mb-8">Dashboard</h1>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-surface p-4 rounded-lg border border-border">
          <div className="flex items-center gap-2 text-text-muted text-sm mb-1">
            <BarChart3 className="w-4 h-4" />
            Daily Used
          </div>
          <div className="text-2xl font-bold">
            {stats?.dailyUsed ?? 0} / {stats?.dailyLimit ?? 100}
          </div>
          <div className="text-sm text-text-muted mt-1">
            {stats?.dailyRemaining ?? 100} remaining today
          </div>
        </div>

        <div className="bg-surface p-4 rounded-lg border border-border">
          <div className="flex items-center gap-2 text-text-muted text-sm mb-1">
            <Key className="w-4 h-4" />
            Active Keys
          </div>
          <div className="text-2xl font-bold">{stats?.activeKeys ?? 0}</div>
          <div className="text-sm text-text-muted mt-1">
            {tier === "free" ? "1 key max on Free" : `${maxKeys} keys max on ${tier.charAt(0).toUpperCase() + tier.slice(1)}`}
          </div>
        </div>

        <div className="bg-surface p-4 rounded-lg border border-border">
          <div className="flex items-center gap-2 text-text-muted text-sm mb-1">
            <CreditCard className="w-4 h-4" />
            Plan
          </div>
          <div className="text-2xl font-bold capitalize">{tier}</div>
          <div className="text-sm text-text-muted mt-1">
            {hasActiveSubscription ? (
              isCancelling ? (
                "Cancels at period end"
              ) : (
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="text-red-400 hover:text-red-300 underline"
                >
                  {cancelling ? "Cancelling..." : "Cancel subscription"}
                </button>
              )
            ) : tier === "free" ? (
              <a href="/pricing" className="text-brand hover:underline">Upgrade</a>
            ) : (
              "—"
            )}
          </div>
          {hasActiveSubscription && !isCancelling && (
            <button
              onClick={handleManageBilling}
              className="text-sm text-text-muted hover:text-text mt-1 block"
            >
              Manage billing →
            </button>
          )}
        </div>
      </div>

      {/* Usage Graph */}
      {usageHistory && usageHistory.length > 0 && (
        <div className="bg-surface p-5 rounded-lg border border-border mb-8">
          <h2 className="text-lg font-semibold mb-4">Last 7 Days</h2>
          <div className="flex items-end gap-2 h-32">
            {usageHistory.map((day) => {
              const maxCount = Math.max(...usageHistory.map((d) => d.requestCount), 1);
              const height = maxCount > 0 ? (day.requestCount / maxCount) * 100 : 0;
              return (
                <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className="w-full bg-brand/20 rounded-t"
                    style={{ height: `${Math.max(height, 4)}%` }}
                  />
                  <span className="text-xs text-text-muted">
                    {day.date.slice(5).replace("-", "/")}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="text-center text-sm text-text-muted mt-2">
            {usageHistory.reduce((sum, d) => sum + d.requestCount, 0).toLocaleString()} total
          </div>
        </div>
      )}

      {/* Total Requests */}
      <div className="bg-surface p-4 rounded-lg border border-border mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-text-muted text-sm">
            <Clock className="w-4 h-4" />
            Total Requests
          </div>
          <span className="text-xl font-bold">{(stats?.totalRequests ?? 0).toLocaleString()}</span>
        </div>
      </div>

      {/* Create API Key */}
      <div className="bg-surface p-5 rounded-lg border border-border mb-8">
        <h2 className="text-lg font-semibold mb-4">Create API Key</h2>
        {atKeyLimit ? (
          <div className="bg-surface-lighter p-4 rounded-lg">
            <p className="text-sm text-text-muted mb-2">
              You've reached the limit of <strong>{maxKeys} API key{maxKeys !== 1 ? "s" : ""}</strong> on the {tier} plan.
            </p>
            {tier === "free" ? (
              <a
                href="/pricing"
                className="text-sm text-brand hover:underline"
              >
                Upgrade to Pro for up to 5 keys →
              </a>
            ) : tier === "pro" ? (
              <a
                href="/pricing"
                className="text-sm text-brand hover:underline"
              >
                Upgrade to Business for up to 20 keys →
              </a>
            ) : (
              <span className="text-sm text-text-muted">
                Contact us for enterprise limits.
              </span>
            )}
          </div>
        ) : (
          <div className="flex gap-3">
            <input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="Key name (e.g. my-app-dev)"
              className="flex-1 bg-background border border-border rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-brand"
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
            <button
              onClick={handleCreate}
              disabled={creating || !newKeyName.trim()}
              className="bg-brand text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand/90 disabled:opacity-50 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {creating ? "Creating..." : "Create"}
            </button>
          </div>
        )}
        {createdKey && (
          <div className="mt-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p className="text-sm text-green-400 mb-2">API Key created! Copy it now - you won't see it again:</p>
            <div className="flex gap-2">
              <code className="flex-1 bg-background border border-border rounded px-3 py-2 text-sm font-mono break-all">
                {createdKey}
              </code>
              <button
                onClick={() => handleCopy(createdKey, "new")}
                className="bg-surface-lighter hover:bg-surface-lighter/80 border border-border rounded-lg px-3 py-2 transition-colors"
              >
                {copiedId === "new" ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <button
              onClick={() => setCreatedKey(null)}
              className="mt-2 text-sm text-text-muted hover:text-text"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Keys list */}
      <div className="border border-border rounded-xl bg-surface-light mb-8">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold">API Keys</h2>
        </div>
        {!keys ? (
          <div className="p-8 text-center text-text-muted">Loading...</div>
        ) : keys.length === 0 ? (
          <div className="p-8 text-center text-text-muted">
            No API keys yet. Create one above to get started.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {keys.map((k) => (
              <div
                key={k._id}
                className={`px-5 py-4 flex items-center justify-between ${
                  k.disabled ? "opacity-50" : ""
                }`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{k.name}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-surface-lighter text-text-muted uppercase">
                      {k.tier}
                    </span>
                    {k.disabled && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                        Revoked
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <code className="text-xs text-text-muted font-mono">{k.keyPreview}</code>
                    <span className="text-xs text-text-muted">
                      {k.requestCount.toLocaleString()} requests
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!k.disabled ? (
                    <button
                      onClick={() => handleRevoke(k._id)}
                      className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Revoke key"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDelete(k._id)}
                      disabled={deletingKeyId === k._id}
                      className="px-3 py-1.5 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors flex items-center gap-1"
                      title="Permanently delete revoked key"
                    >
                      {deletingKeyId === k._id ? (
                        "Deleting..."
                      ) : (
                        <>
                          <X className="w-3 h-3" />
                          Delete
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Playlists */}
      {playlists && playlists.length > 0 && (
        <div className="border border-border rounded-xl bg-surface-light mb-8">
          <div className="px-5 py-4 border-b border-border">
            <h2 className="text-lg font-semibold">Playlists</h2>
          </div>
          <div className="divide-y divide-border">
            {playlists.map((p) => (
              <div key={p._id} className="px-5 py-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <ListMusic className="w-4 h-4 text-brand" />
                    <span className="font-medium text-sm">{p.name}</span>
                    {p.isPublic && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">
                        Public
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-text-muted">
                      {p.trackIds?.length ?? 0} tracks
                    </span>
                    {p.tags && p.tags.length > 0 && (
                      <span className="text-xs text-text-muted">
                        {p.tags.slice(0, 3).join(", ")}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-xs text-text-muted">{timeAgo(p.updatedAt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Requests with Pagination */}
      <div className="bg-surface p-5 rounded-lg border border-border mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Recent Requests</h2>
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-muted">Show:</span>
            <select
              value={logsLimit}
              onChange={(e) => setLogsLimit(Number(e.target.value))}
              className="bg-background border border-border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        {!requestLogs ? (
          <div className="p-8 text-center text-text-muted">Loading...</div>
        ) : requestLogs.length === 0 ? (
          <div className="p-8 text-center text-text-muted">
            No requests yet. Make your first API call!
          </div>
        ) : (
          <div className="space-y-1">
            {requestLogs.map((log) => {
              const isExpanded = expandedLogId === log._id;
              const statusColor =
                log.statusCode >= 200 && log.statusCode < 300
                  ? "text-green-400"
                  : log.statusCode >= 400
                  ? "text-red-400"
                  : "text-yellow-400";

              return (
                <div key={log._id} className="border-b border-border last:border-0">
                  <button
                    onClick={() => setExpandedLogId(isExpanded ? null : log._id)}
                    className="w-full flex items-center gap-4 py-2.5 px-1 hover:bg-surface-lighter/50 rounded transition-colors text-left"
                  >
                    <span className={`text-sm font-mono w-10 shrink-0 ${statusColor}`}>
                      {log.statusCode}
                    </span>
                    <code className="flex-1 text-sm text-text-muted truncate">
                      {log.endpoint}
                    </code>
                    {log.responseSize != null && (
                      <span className="text-xs text-text-muted bg-surface-lighter px-1.5 py-0.5 rounded shrink-0">
                        {log.responseSize} {log.responseSize === 1 ? "result" : "results"}
                      </span>
                    )}
                    <span className="text-sm text-text-muted shrink-0">{log.durationMs}ms</span>
                    <span className="text-sm text-text-muted shrink-0 w-16 text-right">{timeAgo(log.createdAt)}</span>
                    <span className={`text-xs text-text-muted transition-transform ${isExpanded ? "rotate-180" : ""}`}>
                      ▼
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="ml-12 pb-3 space-y-2 text-sm">
                      <div className="bg-background rounded-lg p-3 space-y-1.5">
                        <div className="flex gap-2">
                          <span className="text-text-muted w-20 shrink-0">Method</span>
                          <span className="font-mono">{log.method} {log.endpoint}</span>
                        </div>
                        {log.queryParams && (
                          <div className="flex gap-2">
                            <span className="text-text-muted w-20 shrink-0">Params</span>
                            <code className="font-mono text-xs break-all">
                              {log.queryParams.split("&").map((p: string, i: number) => (
                                <span key={i} className="inline-block bg-surface-lighter px-1.5 py-0.5 rounded mr-1.5 mb-1">
                                  {decodeURIComponent(p)}
                                </span>
                              ))}
                            </code>
                          </div>
                        )}
                        {log.responseSize != null && (
                          <div className="flex gap-2">
                            <span className="text-text-muted w-20 shrink-0">Results</span>
                            <span>{log.responseSize} item{log.responseSize !== 1 ? "s" : ""} returned</span>
                          </div>
                        )}
                        <div className="flex gap-2">
                          <span className="text-text-muted w-20 shrink-0">Duration</span>
                          <span>{log.durationMs}ms</span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-text-muted w-20 shrink-0">Time</span>
                          <span>{new Date(log.createdAt).toLocaleString()}</span>
                        </div>
                        {log.errorMessage && (
                          <div className="flex gap-2">
                            <span className="text-red-400 w-20 shrink-0">Error</span>
                            <span className="text-red-400 font-mono text-xs break-all">{log.errorMessage}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-text-muted">
        Need help? Check out the <a href="/docs" className="text-brand hover:underline">Documentation</a> or <a href="/playground" className="text-brand hover:underline">API Playground</a>.
      </div>
      </div>
    </>
  );
}
